/* Source: /app/components/Visualizer.cl.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { Waypoints } from "lucide-react";
import { useState, useMemo, useCallback, useRef } from "react";
import * as GraphModule from "react-graph-vis";
import { useEffect } from "@jac/runtime";
let Graph = GraphModule.default;
function getGraphOptions(isDarkMode) {
  return {"autoResize": true, "nodes": {"shape": "circle", "size": 30, "font": {"size": 12, "color": "#ffffff", "face": "Arial"}, "borderWidth": 2, "shadow": true, "color": {"background": "#3b82f6", "border": ((isDarkMode && "#4b5563") || "#374151"), "highlight": {"background": "#f59e0b", "border": "#f59e0b"}}}, "edges": {"width": 2, "color": {"color": ((isDarkMode && "#9ca3af") || "#6b7280"), "opacity": 0.8}, "smooth": {"type": "continuous", "forceDirection": "none"}, "arrows": {"to": {"enabled": true, "scaleFactor": 0.5}}, "font": {"size": 12, "align": "top", "color": ((isDarkMode && "#e5e7eb") || "#374151"), "strokeWidth": ((isDarkMode && 2) || 0), "strokeColor": ((isDarkMode && "#1f2937") || "#ffffff")}}, "physics": {"enabled": true, "solver": "forceAtlas2Based", "forceAtlas2Based": {"gravitationalConstant": (-50), "centralGravity": 0.01, "springLength": 120, "springConstant": 0.08, "damping": 0.4, "avoidOverlap": 0.8}, "stabilization": {"enabled": true, "iterations": 200, "updateInterval": 25, "fit": true}}, "interaction": {"hover": true, "tooltipDelay": 200, "zoomView": true, "dragView": true, "dragNodes": true, "hoverConnectedEdges": true}, "layout": {"improvedLayout": true, "hierarchical": {"enabled": false, "sortMethod": "directed"}}};
}
function getNodeColors(label, isRoot) {
  if ((isRoot || (label === "root"))) {
    return {"background": "#ff7743", "border": "#e05a2a"};
  }
  let typePattern = Reflect.construct(RegExp, ["^([a-zA-Z0-9_]+)[(]"]);
  let typeMatch = label.match(typePattern);
  if (typeMatch) {
    let nodeType = typeMatch[1];
    let hashValue = nodeType.split("").reduce((acc, char) => {
      return (char.charCodeAt(0) + ((acc << 5) - acc));
    }, 0);
    let hue = Math.abs((hashValue % 360));
    return {"background": `hsl(${hue}, 60%, 50%)`, "border": `hsl(${hue}, 60%, 35%)`};
  }
  return {"background": "#3b82f6", "border": "#374151"};
}
function getDisplayLabel(label) {
  if ((label === "root")) {
    return "root";
  }
  let namePattern = Reflect.construct(RegExp, ["name='([^']+)'"]);
  let nameMatch = label.match(namePattern);
  if (nameMatch) {
    return nameMatch[1];
  }
  let typePattern = Reflect.construct(RegExp, ["^([a-zA-Z0-9_]+)[(]"]);
  let typeMatch = label.match(typePattern);
  if (typeMatch) {
    return typeMatch[1];
  }
  return label;
}
function getEdgeLabel(label) {
  if (!label) {
    return "";
  }
  let edgePattern = Reflect.construct(RegExp, ["='([a-zA-Z0-9_]+)'"]);
  let edgeMatch = label.match(edgePattern);
  if (edgeMatch) {
    return edgeMatch[1];
  }
  return "";
}
function Visualizer(props) {
  let graphData = props.graphData;
  let loading = (props.loading || false);
  let error = props.error;
  let showHeader = props.showHeader;
  let isDarkMode = (props.isDarkMode || false);
  let shouldShowHeader = (showHeader !== false);
  let hasGraphData = ((graphData && graphData.nodes) && (graphData.nodes.length > 0));
  let [tooltip, setTooltip] = useState({"visible": false, "x": 0, "y": 0, "node": null});
  let networkRef = useRef(null);
  let containerRef = useRef(null);
  let graphOptions = useMemo(() => {
    return getGraphOptions(isDarkMode);
  }, [isDarkMode]);
  useEffect(() => {
    let timer = setTimeout(() => {
      let canvas = document.querySelector("canvas");
      if (canvas) {
        canvas.style.cursor = "grab";
      }
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, [hasGraphData]);
  useEffect(() => {
    if ((!containerRef.current || !networkRef.current)) {
      return;
    }
    let resizeObserver = Reflect.construct(ResizeObserver, [() => {
      if (networkRef.current) {
        networkRef.current.redraw();
        networkRef.current.fit({"animation": {"duration": 300, "easingFunction": "easeInOutQuad"}});
      }
    }]);
    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [hasGraphData]);
  let visGraph = useMemo(() => {
    if (!hasGraphData) {
      return null;
    }
    let nodes = graphData.nodes.map(n => {
      let isRoot = (n.label === "root");
      let colors = getNodeColors(n.label, isRoot);
      return {"id": n.id.toString(), "label": getDisplayLabel(n.label), "color": {"background": colors.background, "border": colors.border, "highlight": {"background": "#f59e0b", "border": "#f59e0b"}}, "title": n.label, "font": {"color": "#ffffff"}};
    });
    let edges = graphData.edges.map((e, index) => {
      return {"id": `edge_${index}`, "from": e.from.toString(), "to": e.to.toString(), "label": getEdgeLabel(e.label), "title": (e.label || "connection"), "arrows": "to"};
    });
    return {"nodes": nodes, "edges": edges};
  }, [graphData, hasGraphData]);
  let findNodeById = useCallback(nodeId => {
    if ((!graphData || !graphData.nodes)) {
      return null;
    }
    return graphData.nodes.find(n => {
      return (n.id.toString() === nodeId);
    });
  }, [graphData]);
  let events = useMemo(() => {
    return {"select": params => {
      console.log("Selected nodes:", params.nodes);
      console.log("Selected edges:", params.edges);
    }, "hoverNode": event => {
      let canvas = event.event.target;
      if (canvas) {
        canvas.style.cursor = "pointer";
      }
      let nodeId = event.node;
      let foundNode = findNodeById(nodeId);
      if (foundNode) {
        setTooltip({"visible": true, "x": event.pointer.DOM.x, "y": event.pointer.DOM.y, "node": foundNode});
      }
    }, "blurNode": event => {
      let canvas = event.event.target;
      if (canvas) {
        canvas.style.cursor = "grab";
      }
      setTooltip({"visible": false, "x": 0, "y": 0, "node": null});
    }, "hoverEdge": event => {
      let canvas = event.event.target;
      if (canvas) {
        canvas.style.cursor = "grab";
      }
    }, "dragStart": event => {
      let canvas = event.event.target;
      if (canvas) {
        canvas.style.cursor = "grabbing";
      }
    }, "dragEnd": event => {
      let canvas = event.event.target;
      if (canvas) {
        canvas.style.cursor = "grab";
      }
    }, "click": event => {
      if ((event.nodes.length === 0)) {
        let canvas = event.event.target;
        if (canvas) {
          canvas.style.cursor = "grab";
        }
      }
    }, "stabilized": () => {
      if (networkRef.current) {
        networkRef.current.fit({"animation": {"duration": 300, "easingFunction": "easeInOutQuad"}});
      }
    }};
  }, [findNodeById]);
  let getNetwork = useCallback(network => {
    networkRef.current = network;
  }, []);
  let graphKey = useMemo(() => {
    if (!hasGraphData) {
      return "empty";
    }
    let themeStr = ((isDarkMode && "dark") || "light");
    return `graph-${graphData.nodes.length}-${graphData.edges.length}-${themeStr}`;
  }, [hasGraphData, graphData, isDarkMode]);
  return __jacJsx("div", {"className": "h-full flex flex-col"}, [(shouldShowHeader && __jacJsx("div", {"className": "flex items-center gap-2 px-3 py-2 border-b border-border bg-background"}, [__jacJsx("div", {"className": "text-xs text-text-secondary"}, ["Graph Visualizer"]), __jacJsx("div", {"className": "flex-1"}, []), __jacJsx("div", {"className": "text-xs text-text-secondary"}, [((hasGraphData && `${graphData.nodes.length} nodes, ${graphData.edges.length} edges`) || "output")])])), (hasGraphData && __jacJsx("div", {"ref": containerRef, "className": "flex-1 relative overflow-hidden bg-surface"}, [__jacJsx(Graph, {"key": graphKey, "graph": visGraph, "options": graphOptions, "events": events, "getNetwork": getNetwork, "style": {"height": "100%", "width": "100%", "position": "absolute", "top": 0, "left": 0, "cursor": "grab"}}, []), ((tooltip.visible && tooltip.node) && __jacJsx("div", {"className": "absolute z-50 bg-surface border border-border rounded-lg shadow-xl p-3 max-w-xs pointer-events-none", "style": {"left": Math.min((tooltip.x + 10), (window.innerWidth - 280)), "top": (tooltip.y - 10)}}, [__jacJsx("div", {"className": "space-y-2"}, [__jacJsx("div", {"className": "border-b border-border pb-2"}, [__jacJsx("h3", {"className": "font-semibold text-text text-sm"}, [getDisplayLabel(tooltip.node.label)]), __jacJsx("p", {"className": "text-xs text-text-secondary font-mono break-all"}, [tooltip.node.label])]), __jacJsx("div", {"className": "text-xs text-text-secondary"}, ["ID:", tooltip.node.id])])]))])), (!hasGraphData && __jacJsx("div", {"className": "flex-1 flex items-center justify-center bg-surface"}, [__jacJsx("div", {"className": "text-text-secondary text-center px-6 flex flex-col items-center justify-center"}, [__jacJsx("div", {"className": "mb-3 text-3xl text-center"}, [__jacJsx(Waypoints, {"className": "w-8 h-8"}, [])]), __jacJsx("div", {"className": "font-medium"}, ["Jac Graph Visualizer"]), __jacJsx("div", {"className": "text-sm text-text-secondary mt-2"}, ["Execute your Jac code to see the graph visualization."])])]))]);
}
export {Visualizer};